import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Stack,
  InputAdornment,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RentalCard from '../../components/rental/RentalCard';
import { rentalAPI, userAPI } from '../../utils/api';
import { isAuthenticated } from '../../utils/auth';
import MapView from '../../components/common/MapView';
import { rentalSearch } from '../../utils/rentalSearch';
import { USC_DPS_POLYGON } from '../../constants/uscDpsPolygon';

const filters = {
  propertyTypes: ['Apartment', 'House', 'Townhouse', 'Condo'],
  amenities: [
    'Furnished',
    'Dishwasher',
    'Air Conditioning',
    'Heating',
    'Parking',
    'Gym',
    'Pool',
    'Pet Friendly',
    'Utilities Included',
    'Internet Included',
  ],
};

const GOOGLE_MAPS_API_KEY = 'AIzaSyABl8lts6BmCwMfw-cVqa1Xy3xDxoEJhUg';

function isUSCSearch(term) {
  if (!term) return false;
  const t = term.toLowerCase();
  return (
    t.includes('usc') ||
    t.includes('university of southern california') ||
    t.includes('南加大')
  );
}

function parseAmenitiesFromUrl(urlParams) {
  const raw = urlParams.get('amenities');
  if (!raw) return [];
  return raw.split(',').map((s) => s.trim()).filter(Boolean);
}

/** 从 URL 查询串构建 API 请求参数（后退/刷新可恢复筛选） */
function buildApiParamsFromSearch(searchString) {
  const urlParams = new URLSearchParams(searchString);
  const params = {};

  const pt = urlParams.get('property_type');
  if (pt) params.property_type = pt;
  const br = urlParams.get('bedrooms');
  if (br) params.bedrooms = br;
  const ba = urlParams.get('bathrooms');
  if (ba) params.bathrooms = ba;
  const amenities = parseAmenitiesFromUrl(urlParams);
  if (amenities.length) params.amenities = amenities;
  const minP = urlParams.get('min_price');
  if (minP) params.min_price = minP;
  const maxP = urlParams.get('max_price');
  if (maxP) params.max_price = maxP;
  const search = urlParams.get('search');
  if (search) params.search = search;

  const lat = urlParams.get('lat');
  const lng = urlParams.get('lng');
  if (lat) params.lat = lat;
  if (lng) params.lng = lng;

  const ne_lat = urlParams.get('ne_lat');
  const ne_lng = urlParams.get('ne_lng');
  const sw_lat = urlParams.get('sw_lat');
  const sw_lng = urlParams.get('sw_lng');
  if (ne_lat && ne_lng && sw_lat && sw_lng) {
    params.ne_lat = ne_lat;
    params.ne_lng = ne_lng;
    params.sw_lat = sw_lat;
    params.sw_lng = sw_lng;
  }

  return params;
}

function RentalListings() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPriceRange, setSelectedPriceRange] = useState('');
  const [customPriceDialogOpen, setCustomPriceDialogOpen] = useState(false);
  const [customMinPrice, setCustomMinPrice] = useState('');
  const [customMaxPrice, setCustomMaxPrice] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState([]);
  const [propertyType, setPropertyType] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [selectedBedrooms, setSelectedBedrooms] = useState('');
  const [selectedBathrooms, setSelectedBathrooms] = useState('');
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRentalId, setSelectedRentalId] = useState(null);
  const [pendingSearch, setPendingSearch] = useState('');
  const [centerLat, setCenterLat] = useState(null);
  const [centerLng, setCenterLng] = useState(null);
  const [hoveredRentalId, setHoveredRentalId] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);
  const [searchTriggered, setSearchTriggered] = useState(false);
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showCustomPrice, setShowCustomPrice] = useState(false);
  const [showCustomPriceDialog, setShowCustomPriceDialog] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());

  /** 一次拉取全部房源（兼容未部署的无分页后端：自动翻页直到没有 next） */
  const fetchRentals = useCallback(async (fetchParams) => {
    setLoading(true);
    setError(null);

    try {
      const allItems = [];
      let page = 1;

      for (;;) {
        const reqParams = { ...(fetchParams || {}) };
        if (page > 1) reqParams.page = page;
        const res = await rentalAPI.getRentals(reqParams);
        const payload = res?.data;

        if (Array.isArray(payload)) {
          setRentals(payload);
          return;
        }

        const chunk = Array.isArray(payload?.results) ? payload.results : [];
        allItems.push(...chunk);
        if (!payload?.next) break;
        page += 1;
      }

      setRentals(allItems);
    } catch (e) {
      setError('Failed to load rentals');
    } finally {
      setLoading(false);
    }
  }, []);

  /** 更新 URL 中的筛选参数（保留地图 bounds / lat/lng / search） */
  const applyUrlFilterPatch = useCallback(
    (patch) => {
      const next = new URLSearchParams(location.search);
      const setOrDelete = (key, val) => {
        if (val != null && val !== '' && !(Array.isArray(val) && val.length === 0)) {
          if (Array.isArray(val)) next.set(key, val.join(','));
          else next.set(key, String(val));
        } else {
          next.delete(key);
        }
      };
      if ('property_type' in patch) setOrDelete('property_type', patch.property_type);
      if ('bedrooms' in patch) setOrDelete('bedrooms', patch.bedrooms);
      if ('bathrooms' in patch) setOrDelete('bathrooms', patch.bathrooms);
      if ('amenities' in patch) setOrDelete('amenities', patch.amenities);
      if ('min_price' in patch) setOrDelete('min_price', patch.min_price);
      if ('max_price' in patch) setOrDelete('max_price', patch.max_price);
      if ('search' in patch) setOrDelete('search', patch.search);
      const qs = next.toString();
      navigate(qs ? `/rentals?${qs}` : '/rentals');
    },
    [location.search, navigate]
  );

  useEffect(() => {
    if (!isAuthenticated()) {
      setFavoriteIds(new Set());
      return;
    }
    let cancelled = false;
    userAPI
      .getFavorites()
      .then((res) => {
        if (cancelled) return;
        setFavoriteIds(new Set((res.data || []).map((r) => Number(r.id))));
      })
      .catch(() => {
        if (!cancelled) setFavoriteIds(new Set());
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleRentalFavoriteClick = useCallback(
    async (rentalId, e) => {
      e?.stopPropagation();
      if (!isAuthenticated()) {
        navigate('/auth', {
          state: { from: `${location.pathname}${location.search}` },
        });
        return;
      }
      try {
        const res = await userAPI.toggleFavorite(rentalId);
        const favorited = res.data?.favorited;
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          const id = Number(rentalId);
          if (favorited) next.add(id);
          else next.delete(id);
          return next;
        });
      } catch (err) {
        console.error(err);
      }
    },
    [navigate, location.pathname, location.search]
  );

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setPropertyType(p.get('property_type') || '');
    setSelectedBedrooms(p.get('bedrooms') || '');
    setSelectedBathrooms(p.get('bathrooms') || '');
    setSelectedAmenities(parseAmenitiesFromUrl(p));
    setMinPrice(p.get('min_price') || '');
    setMaxPrice(p.get('max_price') || '');
    setPendingSearch(p.get('search') || '');

    const lat = p.get('lat');
    const lng = p.get('lng');
    setCenterLat(lat ? parseFloat(lat) : null);
    setCenterLng(lng ? parseFloat(lng) : null);

    fetchRentals(buildApiParamsFromSearch(location.search));
  }, [location.search, fetchRentals]);

  // 让pendingSearch始终与输入框同步，输入即高亮
  const handleSearchInputChange = (event) => {
    setPendingSearch(event.target.value);
  };

  // handleSearch只负责跳转，不再影响pendingSearch
  const handleSearch = async () => {
    await rentalSearch(pendingSearch, navigate);
  };

  const handleSearchInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleAmenityToggle = (amenity) => {
    const newSelection = selectedAmenities.includes(amenity)
      ? selectedAmenities.filter((item) => item !== amenity)
      : [...selectedAmenities, amenity];
    applyUrlFilterPatch({ amenities: newSelection });
  };

  const handleCustomPriceCancel = () => {
    if (!selectedPriceRange || selectedPriceRange === 'custom') {
      setSelectedPriceRange('');
    }
    setCustomPriceDialogOpen(false);
  };

  const handleCustomPriceSubmit = () => {
    const minPrice = parseInt(customMinPrice) || 0;
    const maxPrice = parseInt(customMaxPrice) || 0;
    if (maxPrice > 0 && maxPrice > minPrice) {
      setSelectedPriceRange(`${minPrice}-${maxPrice}`);
    }
    setCustomPriceDialogOpen(false);
  };

  // 移除前端筛选逻辑，直接使用API返回的数据
  const filteredRentals = rentals;

  // 构造markers数组
  const markers = filteredRentals
    .filter(r => r.latitude && r.longitude)
    .map(r => ({ id: String(r.id), lat: r.latitude, lng: r.longitude }));

  // 构造markerData用于InfoWindow
  const markerData = {};
  filteredRentals.forEach(r => { markerData[r.id] = r; });

  // 地图区域变化时更新 URL：合并现有参数，保留 lat/lng/search 等，避免只写 bounds 时冲掉搜索定位
  const handleBoundsChanged = (bounds) => {
    setMapBounds(bounds);
    if (bounds && bounds.ne && bounds.sw) {
      const next = new URLSearchParams(location.search);
      next.set('ne_lat', String(bounds.ne.lat));
      next.set('ne_lng', String(bounds.ne.lng));
      next.set('sw_lat', String(bounds.sw.lat));
      next.set('sw_lng', String(bounds.sw.lng));
      navigate(`/rentals?${next.toString()}`);
    }
  };

  // 判断当前是否允许地图拖动触发API
  const hasBoundsParams = params.get('ne_lat') && params.get('ne_lng') && params.get('sw_lat') && params.get('sw_lng');

  const mapUrlBounds = useMemo(() => {
    const p = new URLSearchParams(location.search);
    const ne_lat = p.get('ne_lat');
    const ne_lng = p.get('ne_lng');
    const sw_lat = p.get('sw_lat');
    const sw_lng = p.get('sw_lng');
    if (!ne_lat || !ne_lng || !sw_lat || !sw_lng) return null;
    const nelat = parseFloat(ne_lat);
    const nelng = parseFloat(ne_lng);
    const swlat = parseFloat(sw_lat);
    const swlng = parseFloat(sw_lng);
    if (![nelat, nelng, swlat, swlng].every(Number.isFinite)) return null;
    return {
      ne: { lat: nelat, lng: nelng },
      sw: { lat: swlat, lng: swlng },
    };
  }, [location.search]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        Available Rentals
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}. Please try again later.
        </Typography>
      )}

      {loading && rentals.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Loading listings…
        </Typography>
      )}

      {/* Search and Filter Section */}
      <Box 
        sx={{ 
          mb: 4, 
          p: 4, 
          background: '#d4b896 !important', 
          borderRadius: 3, 
          boxShadow: '0 8px 25px rgba(139, 111, 71, 0.3)',
        }}
      >
        {/* Search Bar */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="Search by location, property name, or address..."
            value={pendingSearch}
            onChange={handleSearchInputChange}
            onKeyDown={handleSearchInputKeyDown}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: '#8b6f47' }} />
                </InputAdornment>
              ),
              sx: {
                bgcolor: 'rgba(255, 255, 255, 0.95)',
                borderRadius: 2,
                boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
                '& input': {
                  color: '#2d3748',
                  fontSize: '1rem',
                  '&::placeholder': {
                    color: 'rgba(45, 55, 72, 0.6)',
                  },
                },
              },
            }}
          />
          <Button
            variant="contained"
            sx={{ minWidth: 100, bgcolor: '#8b6f47', color: 'white', fontWeight: 600 }}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>

        {/* Filter Controls - 美观一行flex布局 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <FormControl sx={{ width: 220, height: 48 }}>
            <InputLabel>Property Type</InputLabel>
            <Select
              value={propertyType}
              onChange={(e) => {
                const value = e.target.value;
                setPropertyType(value);
                applyUrlFilterPatch({ property_type: value });
              }}
              label="Property Type"
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                height: 48,
                '& .MuiSelect-select': {
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                  color: '#2d3748',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(45, 55, 72, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1.5px solid #8b6f47',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
              }}
            >
              <MenuItem value="">All Types</MenuItem>
              {filters.propertyTypes.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ width: 180, height: 48 }}>
            <InputLabel>Bedrooms</InputLabel>
            <Select
              value={selectedBedrooms}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBedrooms(value);
                applyUrlFilterPatch({ bedrooms: value });
              }}
              label="Bedrooms"
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                height: 48,
                '& .MuiSelect-select': {
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                  color: '#2d3748',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(45, 55, 72, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1.5px solid #8b6f47',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
              }}
            >
              <MenuItem value="">All Bedrooms</MenuItem>
              <MenuItem value="studio">Studio</MenuItem>
              <MenuItem value="1">1 Bedroom</MenuItem>
              <MenuItem value="2">2 Bedrooms</MenuItem>
              <MenuItem value="3">3 Bedrooms</MenuItem>
              <MenuItem value="4">4 Bedrooms</MenuItem>
              <MenuItem value="5">5 Bedrooms</MenuItem>
              <MenuItem value="6">6 Bedrooms</MenuItem>
              <MenuItem value="7+">7+ Bedrooms</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ width: 180, height: 48 }}>
            <InputLabel>Bathrooms</InputLabel>
            <Select
              value={selectedBathrooms}
              onChange={(e) => {
                const value = e.target.value;
                setSelectedBathrooms(value);
                applyUrlFilterPatch({ bathrooms: value });
              }}
              label="Bathrooms"
              sx={{
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                height: 48,
                '& .MuiSelect-select': {
                  height: 48,
                  display: 'flex',
                  alignItems: 'center',
                  fontWeight: 500,
                  color: '#2d3748',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(45, 55, 72, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1.5px solid #8b6f47',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
              }}
            >
              <MenuItem value="">All Bathrooms</MenuItem>
              <MenuItem value="1">1 Bathroom</MenuItem>
              <MenuItem value="2+">2+ Bathrooms</MenuItem>
              <MenuItem value="3+">3+ Bathrooms</MenuItem>
              <MenuItem value="4+">4+ Bathrooms</MenuItem>
            </Select>
          </FormControl>
          <TextField
            variant="outlined"
            label="Min Price"
            type="number"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{
              width: 120,
              '& .MuiOutlinedInput-root': {
                height: 48,
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                fontWeight: 500,
                color: '#2d3748',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(45, 55, 72, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1.5px solid #8b6f47',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
          <TextField
            variant="outlined"
            label="Max Price"
            type="number"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            sx={{
              width: 120,
              '& .MuiOutlinedInput-root': {
                height: 48,
                bgcolor: 'rgba(255,255,255,0.7)',
                borderRadius: '10px',
                fontWeight: 500,
                color: '#2d3748',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: '1px solid rgba(45, 55, 72, 0.3)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: '1.5px solid #8b6f47',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: '2px solid #8b6f47',
                },
              },
              '& .MuiOutlinedInput-input': {
                py: 0,
                display: 'flex',
                alignItems: 'center',
              },
            }}
          />
          <Button
            variant="outlined"
            sx={{ width: 80, height: 48, borderRadius: '10px', fontWeight: 600, color: '#8b6f47', borderColor: '#8b6f47', fontSize: '1rem', display: 'flex', alignItems: 'center' }}
            onClick={() =>
              applyUrlFilterPatch({ min_price: minPrice, max_price: maxPrice })
            }
          >
            Apply
          </Button>
        </Box>

        {/* Amenities */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            gutterBottom 
            sx={{ 
              color: '#2d3748', 
              mb: 2, 
              fontWeight: 600,
            }}
          >
            Amenities
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {filters.amenities.map((amenity) => (
              <Chip
                key={amenity}
                label={amenity}
                onClick={() => handleAmenityToggle(amenity)}
                sx={{
                  m: 0.5,
                  bgcolor: selectedAmenities.includes(amenity) 
                    ? 'rgba(255, 255, 255, 0.9)' 
                    : 'rgba(255, 255, 255, 0.5)',
                  color: '#2d3748',
                  borderRadius: 2,
                  fontWeight: 500,
                  border: selectedAmenities.includes(amenity) 
                    ? '2px solid #8b6f47'
                    : '1px solid rgba(45, 55, 72, 0.3)',
                  boxShadow: selectedAmenities.includes(amenity) 
                    ? '0 2px 8px rgba(0, 0, 0, 0.15)'
                    : 'none',
                  '&:hover': { 
                    bgcolor: selectedAmenities.includes(amenity) 
                      ? 'rgba(255, 255, 255, 1)' 
                      : 'rgba(255, 255, 255, 0.7)',
                    transform: 'translateY(-1px)',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                  },
                  transition: 'all 0.3s ease',
                }}
              />
            ))}
          </Stack>

        </Box>


      </Box>

      <Dialog open={customPriceDialogOpen} onClose={handleCustomPriceCancel}>
        <DialogTitle>Set Custom Price Range</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', gap: 2 }}>
            <TextField
              label="Min Price"
              type="number"
              value={customMinPrice}
              onChange={(e) => setCustomMinPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
            <TextField
              label="Max Price"
              type="number"
              value={customMaxPrice}
              onChange={(e) => setCustomMaxPrice(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCustomPriceCancel}>Cancel</Button>
          <Button onClick={handleCustomPriceSubmit} variant="contained">Apply</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showCustomPriceDialog} onClose={() => setShowCustomPriceDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Custom Price Range</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <TextField
              label="Min Price"
              type="number"
              value={minPrice}
              onChange={e => setMinPrice(e.target.value)}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }}
            />
            <TextField
              label="Max Price"
              type="number"
              value={maxPrice}
              onChange={e => setMaxPrice(e.target.value)}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.7)', borderRadius: 2 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCustomPriceDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#8b6f47', color: 'white', fontWeight: 600 }}
            onClick={() => {
              setShowCustomPriceDialog(false);
              setPriceRange('custom');
              applyUrlFilterPatch({ min_price: minPrice, max_price: maxPrice });
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', gap: 3, mt: 3 }}>
        <Box sx={{ 
          width: '450px', 
          flexShrink: 0
        }}>
          <Stack spacing={2}>
            {filteredRentals.map((rental) => (
              <RentalCard
                key={rental.id}
                rental={rental}
                isFavorited={favoriteIds.has(Number(rental.id))}
                onFavoriteClick={handleRentalFavoriteClick}
                selected={selectedRentalId === rental.id}
                hovered={
                  hoveredRentalId != null &&
                  Number(hoveredRentalId) === Number(rental.id)
                }
                onListingClick={(id) => setSelectedRentalId(id)}
                onMouseEnter={() => setHoveredRentalId(String(rental.id))}
                onMouseLeave={() => setHoveredRentalId(null)}
              />
            ))}
          </Stack>
        </Box>
        
        <Box sx={{ flex: 2, minWidth: 600, display: { xs: 'none', md: 'block' } }}>
          <Paper
            sx={{
              height: '800px',
              position: 'sticky',
              top: 24,
              backgroundColor: 'grey.100',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: '100%', height: '100%' }}>
              <MapView 
                key={centerLat != null && centerLng != null ? `${centerLat},${centerLng}` : 'default'}
                markers={markers} 
                selectedId={selectedRentalId} 
                hoveredId={hoveredRentalId}
                onMarkerClick={(marker) => setSelectedRentalId(marker.id)}
                onMarkerHover={(marker) => setHoveredRentalId(marker ? marker.id : null)}
                centerLat={centerLat}
                centerLng={centerLng}
                onBoundsChanged={handleBoundsChanged}
                markerData={markerData}
                allowBoundsChange={true}
                suppressAutoFit={!!hasBoundsParams}
                initialUrlBounds={mapUrlBounds}
                uscPolygon={USC_DPS_POLYGON}
              />
            </Box>
          </Paper>
        </Box>
      </Box>
    </Container>
  );
}

export default RentalListings; 